import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { BookingCardSkeleton } from '../../components/ui/skeletons';
import { customerDemoBookingService } from '../../services/customerDemoBookingService';
import { useToast } from '../../hooks/use-toast';
import { Calendar, Clock, MapPin, User, Loader2, CalendarX, ChevronRight } from 'lucide-react';

const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'confirmed':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'completed':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${ampm}`;
};

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await customerDemoBookingService.getBookings();
      if (response.success) {
        setBookings(response.data?.bookings || []);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch bookings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'upcoming') {
      return ['pending', 'confirmed'].includes(booking.status);
    }
    return booking.status === activeTab;
  });

  const handleCancelClick = (booking) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!bookingToCancel) return;

    setCancelling(true);
    try {
      const response = await customerDemoBookingService.cancelBooking(bookingToCancel.id);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Booking cancelled successfully',
        });
        setCancelDialogOpen(false);
        setBookingToCancel(null);
        fetchBookings();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to cancel booking',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleViewDetails = (bookingId) => {
    navigate(`/customer/bookings/${bookingId}`);
  };

  const getTabCount = (tab) => {
    if (tab === 'all') return bookings.length;
    if (tab === 'upcoming') {
      return bookings.filter((b) => ['pending', 'confirmed'].includes(b.status)).length;
    }
    return bookings.filter((b) => b.status === tab).length;
  };

  const renderEmptyState = () => {
    const emptyMessages = {
      all: {
        title: 'No bookings yet',
        description: 'You haven\'t booked any demo sessions yet',
      },
      upcoming: {
        title: 'No upcoming bookings',
        description: 'You have no pending or confirmed demo sessions',
      },
      completed: {
        title: 'No completed bookings',
        description: 'You haven\'t completed any demo sessions yet',
      },
      cancelled: {
        title: 'No cancelled bookings',
        description: 'You haven\'t cancelled any demo sessions',
      },
    };

    const message = emptyMessages[activeTab] || emptyMessages.all;

    return (
      <Card className="text-center py-12">
        <CardContent>
          <CalendarX className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{message.title}</h3>
          <p className="text-gray-500">{message.description}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">My Demo Bookings</h1>
        <p className="text-gray-600">Manage your demo session bookings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="all">
            All ({getTabCount('all')})
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Upcoming ({getTabCount('upcoming')})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({getTabCount('completed')})
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled ({getTabCount('cancelled')})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <BookingCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredBookings.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBookings.map((booking) => (
                <Card key={booking.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={booking.tutor?.photo} alt={booking.tutor?.name} />
                          <AvatarFallback className="bg-gradient-to-br from-blue-600 to-teal-500 text-white">
                            {booking.tutor?.name?.charAt(0) || 'T'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">{booking.tutor?.name || 'Tutor'}</CardTitle>
                          <p className="text-sm text-gray-500">
                            {booking.tutor?.subjects?.slice(0, 2).join(', ') || 'Subjects'}
                          </p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(booking.status)}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4 text-blue-600" />
                      <span>Student: {booking.student?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4 text-green-600" />
                      <span>{formatDate(booking.demoDate || booking.demo_date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4 text-purple-600" />
                      <span>{formatTime(booking.demoTime || booking.demo_time)}</span>
                    </div>
                    {(booking.location || booking.address) && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 text-red-600" />
                        <span className="truncate">{booking.location || booking.address}</span>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1 gap-1"
                        size="sm"
                        onClick={() => handleViewDetails(booking.id)}
                      >
                        View Details
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                      {['pending', 'confirmed'].includes(booking.status) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelClick(booking)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this demo session with {bookingToCancel?.tutor?.name || 'the tutor'}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>No, Keep It</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Booking'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyBookings;
